"use client";
import { FC, useState } from "react";
import clsx from "clsx";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import TInput from "@/components/atoms/input/TInput";
import TSelect from "@/components/atoms/select/TSelect";
import TTextArea from "@/components/atoms/text-area/TTextArea";
import TCheckbox from "@/components/atoms/checkbox/TCheckbox";
import TButton from "@/components/atoms/buttons/TButton";
import axios from "axios";
import { trimEnd } from "lodash";
import { env } from "@/env.mjs";
import { IContactFormRequest } from "@/api/dtos/contact-us.type";
import { contactUsPositionList } from "@/constants/contact-us-form";
import { contactUsSchema } from "@/app/api/validationSchemas";
import { tNotifications } from "@/components/atoms/notification/TNotification";

import GoogleReCaptcha from "../common/GoogleReCaptcha";
import useGoogleReCaptcha from "@/hooks/useGoogleReCaptcha";
import TPhoneInput from "@/components/atoms/phone-input/TPhoneInput";

// -------------------------------------------------------------

type IContactUsFormProps = {};

type IDefaultValues = {
  firstName: string;
  lastName: string;
  companyName: string;
  workEmail: string;
  position: string | null;
  specificPosition?: string;
  description: string;
  phone?: string;
  emailConcent?: boolean;
};

const defaultValues: IDefaultValues = {
  firstName: "",
  lastName: "",
  companyName: "",
  workEmail: "",
  position: null,
  specificPosition: "",
  phone: "",
  description: "",
  emailConcent: true,
};

const ContactUsForm: FC<IContactUsFormProps> = () => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const {
    reCaptchaRef,
    showError: showReCaptchaError,
    isReCaptchaAvailable,
    isRecaptchaVarified,
    resetReCaptcha,
    removeReCaptchaError,
  } = useGoogleReCaptcha();

  const onSubmit = async (formData: IDefaultValues) => {
    if (!isRecaptchaVarified()) return;

    try {
      setIsSubmitting(true);
      removeReCaptchaError();

      await axios.post<IContactFormRequest>(
        `${trimEnd(env.NEXT_PUBLIC_SITE_URL, "/")}/api/contact-us`,
        formData,
      );
      reset();
      tNotifications.success({
        title: "Success",
        message: "Form submitted successfully!",
      });
    } catch (error) {
      tNotifications.error({
        title: "Error",
        message: "Oops! Something went wrong. Please try again",
      });
    } finally {
      setIsSubmitting(false);
      resetReCaptcha();
    }
  };

  const { handleSubmit, reset, control, setValue, watch } = useForm({
    defaultValues,
    resolver: yupResolver(contactUsSchema),
  });

  return (
    <section className="w-full bg-secondary-50 p-6 !pb-3 lg:max-w-[550px] lg:p-8 lg:!pb-5 xl:p-12 xl:!pb-9">
      <h4 className="mb-7 text-h4 font-semibold text-black-900">
        How can we help you ?
      </h4>
      <p className="mb-7 text-base text-gray-500">
        Fill in this form or send us an e-mail with your inquiry.
      </p>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="text-re flex w-full flex-col justify-center gap-4"
        id="contact-us-form"
      >
        <TInput name="firstName" control={control} placeholder="First Name *" />
        <TInput name="lastName" control={control} placeholder="Last Name *" />
        <TInput
          name="companyName"
          control={control}
          placeholder="Organization Name *"
        />
        <TInput name="workEmail" control={control} placeholder="Work Email *" />
        <TSelect
          name="position"
          control={control}
          placeholder="Position *"
          data={contactUsPositionList}
        />
        {watch("position") === "Other" && (
          <TInput
            name="specificPosition"
            control={control}
            placeholder="Specify your position *"
          />
        )}
        <TPhoneInput control={control} name="phone" />
        <TTextArea
          name="description"
          control={control}
          placeholder="Description *"
        />
        <TCheckbox
          label="I agree to store and use my email address for multiple purposes"
          checked={watch("emailConcent")}
          onChange={(e) => setValue("emailConcent", e.currentTarget.checked)}
        />

        {/* Google reCAPTCHA component */}
        {isReCaptchaAvailable && (
          <GoogleReCaptcha ref={reCaptchaRef} showError={showReCaptchaError} />
        )}

        <TButton
          id="contact-us-form-submit-button"
          type="submit"
          variant="primary"
          className={clsx("h-fit", isSubmitting && "!cursor-not-allowed")}
          disabled={isSubmitting}
        >
          Subscribe
        </TButton>
      </form>
    </section>
  );
};

export default ContactUsForm;
